import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  InputNumber,
  message,
  Space,
  Tag,
} from 'antd';
import {
  FacebookFilled,
  GoogleOutlined,
} from '@ant-design/icons';
import { pixelService, PixelConfig, EventType } from '../services/pixel';

interface PostbackModalProps {
  visible: boolean;
  visitorId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const PostbackModal: React.FC<PostbackModalProps> = ({
  visible,
  visitorId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pixelConfigs, setPixelConfigs] = useState<{ facebook?: PixelConfig[]; google?: PixelConfig[] }>({});
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');

  useEffect(() => {
    if (visible) {
      fetchPixelConfigs();
      form.resetFields();
      setEventTypes([]);
      setSelectedPlatform('');
    }
  }, [visible]);

  const fetchPixelConfigs = async () => {
    try {
      const data = await pixelService.getPixelConfigs();
      setPixelConfigs(data);
    } catch (error) {
      message.error('获取像素配置失败');
    }
  };

  const handlePixelChange = async (pixelConfigId: number) => {
    // 找到选中的像素配置
    let platform = '';
    const allConfigs = [...(pixelConfigs.facebook || []), ...(pixelConfigs.google || [])];
    const selectedConfig = allConfigs.find(c => c.id === pixelConfigId);
    
    if (selectedConfig) {
      // 根据像素配置判断平台
      platform = pixelConfigs.facebook?.some(c => c.id === pixelConfigId) ? 'facebook' : 'google';
      setSelectedPlatform(platform);
      
      // 获取事件类型
      try {
        const events = await pixelService.getEventTypes(platform);
        setEventTypes(events);
        form.setFieldValue('event_type', undefined);
      } catch (error) {
        message.error('获取事件类型失败');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await pixelService.postback({
        visitor_id: visitorId,
        pixel_config_id: values.pixel_config_id,
        event_type: values.event_type,
        event_value: values.event_value,
        currency: values.currency || 'USD',
      });

      message.success('回传成功');
      onClose();
      onSuccess?.();
    } catch (error: any) {
      message.error(error.message || '回传失败');
    } finally {
      setLoading(false);
    }
  };

  // 构建像素选项
  const pixelOptions = [
    ...(pixelConfigs.facebook || []).map(c => ({
      value: c.id,
      label: (
        <Space>
          <FacebookFilled style={{ color: '#1877f2' }} />
          {c.pixel_name || c.pixel_code}
        </Space>
      ),
    })),
    ...(pixelConfigs.google || []).map(c => ({
      value: c.id,
      label: (
        <Space>
          <GoogleOutlined style={{ color: '#4285f4' }} />
          {c.pixel_name || c.pixel_code}
        </Space>
      ),
    })),
  ];

  const hasPixelConfigs = pixelOptions.length > 0;

  return (
    <Modal
      title="手动像素回传"
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      okText="确认回传"
      cancelText="取消"
      width={480}
    >
      {!hasPixelConfigs ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p>暂无可用像素配置</p>
          <p style={{ color: '#999' }}>请先在「增值功能 → 像素配置」中添加像素</p>
        </div>
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item
            name="pixel_config_id"
            label="选择像素"
            rules={[{ required: true, message: '请选择像素' }]}
          >
            <Select
              placeholder="请选择像素"
              options={pixelOptions}
              onChange={handlePixelChange}
            />
          </Form.Item>

          <Form.Item
            name="event_type"
            label="选择事件类型"
            rules={[{ required: true, message: '请选择事件类型' }]}
          >
            <Select
              placeholder="请先选择像素"
              disabled={!selectedPlatform}
              options={eventTypes.map(e => ({
                value: e.code,
                label: `${e.name} (${e.code})`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="event_value"
            label="事件价值 (可选)"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="如: 99.00"
              min={0}
              precision={2}
              addonAfter={
                <Form.Item name="currency" noStyle initialValue="USD">
                  <Select style={{ width: 80 }}>
                    <Select.Option value="USD">USD</Select.Option>
                    <Select.Option value="CNY">CNY</Select.Option>
                    <Select.Option value="EUR">EUR</Select.Option>
                  </Select>
                </Form.Item>
              }
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default PostbackModal;
